import { useSelector, useDispatch } from 'react-redux';
import { CloseOutlined, PlusCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Flex, Form, Switch, Typography, Input, Select, Button, Row, Col, Radio } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setSelectedStep, setShowCondition, setWorkflow } from '../../redux/slices/connectorSlice';
import UserRoleDropdown from '../UserRoleDropdown';
import { FORM_LANGUAGE_OPTIONS } from '../../utils/formTranslations';
import {
    DEFAULT_LANGUAGE,
    extractNodeTranslatableValues,
    buildNodeTranslationDraft,
} from '../../utils/workflowTranslations';
import { trimStringValues } from '../../utils/trimStrings';
import { getAllWidgets } from '../../utils/formsHelper';
import { getPreviousFormsForConditions } from '../../utils/workflowHelper';

// Field types whose values can be resolved into someone to share the request
// with (an email typed into a text field, or a picked dropdown option).
const SHARE_TO_FIELD_TYPES = ["inputForm", "dropdownField"]

// The share language is taken from a dropdown field the requester already
// answered upstream, so only dropdowns can carry it.
const SHARE_LANGUAGE_FIELD_TYPES = ["dropdownField"]

// `shareTo` held a single field id before it became multi-select; both shapes
// are normalised to an array so workflows saved earlier keep their selection.
const toShareToArray = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean)
    return typeof value === "string" && value.trim() !== "" ? [value] : []
}

const periods = [
    { label: "Days", value: "day" },
    { label: "Weeks", value: "week" },
    { label: "Months", value: "month" },
    { label: "Quarters", value: "quarter" },
    { label: "Years", value: "year" }
]


const StepDetailsForm = () => {
    const [stepDetails, setStepDetails] = useState({})
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const formValues = Form.useWatch([], form);

    const { selectedStep, workflow, workflowForms } = useSelector(state => state.connector);
    // Which language the panel is editing. The default language edits the
    // canonical step config; other languages edit `data.translations[lng]` and
    // only expose the translatable text (step name, approve/reject labels).
    const [editLang, setEditLang] = useState(DEFAULT_LANGUAGE)
    const isDefaultLang = editLang === DEFAULT_LANGUAGE

    // Reset to the default language whenever a different step is selected.
    useEffect(() => {
        setEditLang(DEFAULT_LANGUAGE)
    }, [selectedStep?.id])

    const selectedStepId = selectedStep?.id

    // The form attached to this step, if any. `formData` lives on the node
    // itself (not on `node.data`), and the items only exist on the fetched
    // form, so both have to be looked up.
    const stepForm = useMemo(() => {
        const stepNode = (workflow?.nodes || []).find(node => node.id === selectedStepId)
        const formId = stepNode?.formData?.formId
        if (!formId) return null
        return (workflowForms || []).find(form => (form.formId || form.id) === formId) || null
    }, [workflow?.nodes, workflowForms, selectedStepId])

    // Shareable fields come from the forms upstream of this step - those are
    // the only ones already filled in by the time the step is reached.
    const previousForms = useMemo(() => {
        if (!selectedStepId || !workflow?.nodes?.length) return []
        // The helper only walks upstream from the node id.
        const previousNodes = getPreviousFormsForConditions(workflow.nodes, workflow.edges || [], { id: selectedStepId })
        const forms = []
        previousNodes.forEach(node => {
            const formId = node?.formData?.formId
            if (!formId) return
            const nodeForm = (workflowForms || []).find(form => (form.formId || form.id) === formId)
            if (nodeForm && !forms.includes(nodeForm)) {
                forms.push(nodeForm)
            }
        })
        return forms
    }, [workflow?.nodes, workflow?.edges, workflowForms, selectedStepId])

    const shareToOptions = useMemo(() => {
        let options = []
        previousForms.forEach(previousForm => {
            const fields = getAllWidgets(previousForm.items || [])
                .filter(field => SHARE_TO_FIELD_TYPES.includes(field.formType))
                .map(field => ({
                    label: previousForm?.formHeader?.label ? `${field.label} - ${previousForm.formHeader.label}` : field.label,
                    value: field.id,
                }))
            options = options.concat(fields)
        })
        return options
    }, [previousForms])

    // The language is taken from an upstream dropdown field whose answer
    // resolves to a language at run time.
    const shareLanguageOptions = useMemo(() => {
        let options = []
        previousForms.forEach(previousForm => {
            const fields = getAllWidgets(previousForm.items || [])
                .filter(field => SHARE_LANGUAGE_FIELD_TYPES.includes(field.formType))
                .map(field => ({
                    label: previousForm?.formHeader?.label ? `${field.label} - ${previousForm.formHeader.label}` : field.label,
                    value: field.id,
                }))
            options = options.concat(fields)
        })
        return options
    }, [previousForms])

    useEffect(() => {
        if (selectedStep) {
            const nextDetails = { ...selectedStep }
            if (nextDetails.approvalStep) {
                nextDetails.approveLabel = nextDetails.approveLabel || "Approve"
                nextDetails.rejectLabel = nextDetails.rejectLabel || "Reject"
            }
            setStepDetails(nextDetails)
        }
    }, [selectedStep])


    useEffect(() => {
        let containerEle = document.getElementById("step-container");
        if (selectedStep.name) {
            if (containerEle) {
                containerEle.style.display = "flex";
            }
            const nextDetails = {
                ...selectedStep,
                approveLabel: selectedStep?.approvalStep ? (selectedStep.approveLabel || "Approve") : selectedStep.approveLabel,
                rejectLabel: selectedStep?.approvalStep ? (selectedStep.rejectLabel || "Reject") : selectedStep.rejectLabel,
                // Seeded explicitly so the previous step's selection does not
                // linger - setFieldsValue only touches the keys it is given.
                shareTo: toShareToArray(selectedStep.shareTo),
                shareLanguage: selectedStep.shareLanguage || undefined,
            }
            // For a non-default language, load this language's translated text
            // (blank when untranslated) so the author can fill it in. All other
            // settings keep their shared canonical values.
            form.setFieldsValue(isDefaultLang ? nextDetails : buildNodeTranslationDraft(nextDetails, editLang))
            setStepDetails({ ...nextDetails })
        } else {
            containerEle.style.display = "none"
        }
    }, [selectedStep, form, editLang])

    const saveStepDetails = () => {
        const latestValues = trimStringValues(form.getFieldsValue(true));
        // Translation mode: persist only the translatable text into
        // `data.translations[lng]`; shared settings stay defined in the default
        // language. Merge (rather than replace) so an empty field clears that
        // translation while leaving the others intact.
        if (!isDefaultLang) {
            const nextTranslations = {
                ...(stepDetails.translations || {}),
                [editLang]: {
                    ...(stepDetails.translations?.[editLang] || {}),
                    ...extractNodeTranslatableValues(latestValues),
                },
            }
            const updatedNodes = workflow.nodes.map(node => {
                if (node.id === stepDetails.id) {
                    return { ...node, data: { ...node.data, translations: nextTranslations } }
                }
                return { ...node }
            })
            dispatch(setWorkflow({ ...workflow, nodes: updatedNodes }))
            dispatch(setSelectedStep({}))
            return
        }
        const mergedValues = { ...stepDetails, ...latestValues };
        // Share To is only offered when the step owns a form; drop any selection
        // left behind by a form that has since been detached or deleted. The
        // language only means anything alongside a chosen field, so it follows it.
        if (!stepForm) {
            delete mergedValues.shareTo
            delete mergedValues.shareLanguage
        } else {
            const shareTo = toShareToArray(mergedValues.shareTo)
            mergedValues.shareTo = shareTo
            mergedValues.shareLanguage = shareTo.length
                ? (mergedValues.shareLanguage || "")
                : ""
        }
        if (stepDetails.approvalStep) {
            mergedValues.approveLabel = mergedValues.approveLabel || "Approve";
            mergedValues.rejectLabel = mergedValues.rejectLabel || "Reject";
        }
        const updatedNodes = workflow.nodes.map(node => {
            if (node.id === stepDetails.id) {
                let reqDetails = { ...node, data: { ...mergedValues } }
                if (mergedValues.approverType === "conditional") {
                    reqDetails.data.assignTo = { user: [], role: [], group: [] }
                }
                return { ...reqDetails }
            } else {
                return { ...node }
            }
        })
        dispatch(setWorkflow({ ...workflow, nodes: updatedNodes }))
        const currentApproverType = latestValues.approverType || formValues?.approverType;
        if (selectedStep?.approverType !== "conditional" && currentApproverType === "conditional") {
            dispatch(setShowCondition(true))
            dispatch(setSelectedStep({ ...selectedStep, data: { ...stepDetails } }))
        } else {
            dispatch(setSelectedStep({}))
        }
    }

    const closeStepDetails = () => {
        dispatch(setSelectedStep({}))
    }
    const handleChange = (key, val) => {
        let updatedDetails = { ...stepDetails }
        if (key === "deadline") {
            if (val) {
                updatedDetails.count = 1
                updatedDetails.period = 'day'
                form.setFieldsValue({ count: 1, period: 'day' })
            } else {
                if (updatedDetails.count) {
                    delete updatedDetails.count
                    delete updatedDetails.period
                    form.setFieldsValue({ count: undefined, period: undefined })
                }
            }
        }
        if (key === "count" || key === "period") {
            form.setFieldsValue({ [key]: val })
        }
        if (key === "approvalStep") {
            if (!val) {
                // Reset form fields completely when approval step is turned off
                form.resetFields(['assignmentType', 'approverType', 'approveLabel', 'rejectLabel']);
                // Clear from stepDetails as well
                delete updatedDetails.assignmentType;
                delete updatedDetails.approverType;
                delete updatedDetails.assignTo;
                delete updatedDetails.approveLabel;
                delete updatedDetails.rejectLabel;
                
                // Update workflow immediately to reflect changes in the card
                const updatedNodes = workflow.nodes.map(node => {
                    if (node.id === stepDetails.id) {
                        const nodeData = { ...stepDetails, ...updatedDetails, [key]: val };
                        delete nodeData.assignmentType;
                        delete nodeData.approverType;
                        delete nodeData.assignTo;
                        delete nodeData.approveLabel;
                        delete nodeData.rejectLabel;
                        return { ...node, data: nodeData };
                    }
                    return node;
                });
                dispatch(setWorkflow({ ...workflow, nodes: updatedNodes }));
            }
            if (val) {
                const approveLabel = updatedDetails.approveLabel || "Approve"
                const rejectLabel = updatedDetails.rejectLabel || "Reject"
                updatedDetails.approveLabel = approveLabel
                updatedDetails.rejectLabel = rejectLabel
                form.setFieldsValue({ approveLabel, rejectLabel })
            }
        }
        setStepDetails({ ...updatedDetails, [key]: val })
        form.setFieldsValue({ [key]: val })
    }


    const onSelect = (values) => {
        form.setFieldValue("assignTo", { user: values.user || [], role: values.role || [], group: values.group || [], userPicker: values.userPicker || [] })
        setStepDetails(prev => ({ ...prev, assignTo: { user: values.user, role: values.role, group: values.group, userPicker: values.userPicker } }))
    }


    const onNotifySelect = (values) => {
        form.setFieldValue("notifyTo", { user: values.user || [], role: values.role || [], group: values.group || [], userPicker: values.userPicker || [] })

        setStepDetails(prev => ({ ...prev, notifyTo: { user: values.user, role: values.role, group: values.group, userPicker: values.userPicker } }))
    }

    return (
        <div className='step-details-container' id="step-container">
            <Flex className='step-details-header' align="center" justify="space-between">
                <Typography.Title level={5} className='margin-0'>{t("Step Details")}</Typography.Title>
                <CloseOutlined className="icon-medium" onClick={closeStepDetails} />
            </Flex>
            <Flex vertical justify='space-between'>
                <div className='step-details-body'>
                    <Form form={form} onFinish={saveStepDetails} labelAlign="left" layout="vertical" style={{ height: "100%" }}>
                        <Flex vertical justify="space-between" style={{ height: "100%" }}>
                            <div>
                                <div style={{ marginBottom: "16px" }}>
                                    <Select
                                        style={{ width: "100%" }}
                                        value={editLang}
                                        onChange={setEditLang}
                                        options={FORM_LANGUAGE_OPTIONS}
                                    />
                                    {
                                        !isDefaultLang &&
                                        <div className="text-small text-color-secondary" style={{ marginTop: 4 }}>
                                            {t("Editing translation — only the step name and approve/reject labels are saved for this language. Other settings are shared across languages.")}
                                        </div>
                                    }
                                </div>
                                <Form.Item label={t("Step Name")} name="name" rules={isDefaultLang ? [
                                    {
                                        required: true,
                                        whitespace: true,
                                        message: t('Step name is required')
                                    }
                                ] : []}>
                                    <Input />
                                </Form.Item>
                                {
                                  isDefaultLang && <>
                                <Flex style={{ marginBottom: "16px" }} align='center' >
                                    <Switch checkedChildren={true} unCheckedChildren={false} value={stepDetails.deadline || false} onChange={(checked) => handleChange("deadline", checked)} size='small' /> <span className="text-color-primary" style={{ marginLeft: "0.5rem" }}>{t("Deadline")}</span>
                                </Flex>
                                {
                                    stepDetails?.deadline ?
                                        <Row gutter={8}>
                                            <Col span={8}>
                                                <div style={{ width: '100%', border: '1px solid rgba(217, 217, 217, 1)', display: 'flex', justifyContent: 'space-around', borderRadius: '10px' }} >
                                                    <Button
                                                        style={{ border: 'none', padding: '0px', height: "20px", marginTop: "3px", cursor: stepDetails?.count === 0 ? "not-allowed" : "pointer" }}
                                                        onClick={() => {
                                                            const currentDay = stepDetails?.count || 0;
                                                            if (currentDay === 0) { return }
                                                            handleChange('count', currentDay - 1)
                                                        }}>
                                                        <MinusCircleOutlined style={{ marginLeft: "3px", padding: '0px', color: stepDetails?.count === 0 ? 'grey' : 'rgba(20, 27, 203, 1)' }} />
                                                    </Button>
                                                    <Input type="number"
                                                        placeholder={t('Number')}
                                                        style={{ width: '70%', border: 'none' }}
                                                        // min={0}
                                                        controls={true} value={stepDetails.count} onChange={(e) => handleChange('count', e.target.value)} />


                                                    <Button
                                                        style={{ border: 'none', padding: '0px', height: "20px", marginTop: "3px" }}
                                                        onClick={() => {
                                                            const currentDay = stepDetails?.count || 0;
                                                            handleChange('count', currentDay + 1)
                                                        }}>
                                                        <PlusCircleOutlined style={{ marginRight: "4px", padding: '0px', color: "rgba(20, 27, 203, 1)" }} />
                                                    </Button>
                                                </div>
                                            </Col>
                                            <Col span={8}>
                                                <Select style={{ width: "100%" }} options={periods.map(option => ({ value: option.value, label: t(option.label) }))} value={stepDetails.period} onChange={(val) => handleChange("period", val)} />
                                            </Col>
                                        </Row>
                                        : null
                                }
                                </>
                                }

                                {
                                  isDefaultLang &&
                                <Flex style={{ margin: "16px 0" }} align='center' >
                                    <Switch checkedChildren={true} unCheckedChildren={false} value={stepDetails.approvalStep || false} onChange={(checked) => handleChange("approvalStep", checked)} size='small' /> <span className="text-color-primary" style={{ marginLeft: "0.5rem" }}>{t("This is an approval step")}</span>
                                </Flex>
                                }

                                {
                                    stepDetails?.approvalStep ?
                                        <Row gutter={8}>
                                            <Col span={12}>
                                                <Form.Item
                                                    label={t("Approve Label")}
                                                    name="approveLabel"
                                                >
                                                    <Input placeholder={t('Approve')} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item
                                                    label={t("Reject Label")}
                                                    name="rejectLabel"
                                                >
                                                    <Input placeholder={t('Reject')} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        : null
                                }

                                {
                                  isDefaultLang && <>
                                {/* {
                                    stepDetails?.approvalStep ?
                                        <> */}
                                            <Form.Item
                                                label={t("Assignment Type")}
                                                name="assignmentType" 
                                                // rules={stepDetails?.approvalStep ? [
                                                //     {
                                                //         required: true,
                                                //         message: 'Assignment type is required'
                                                //     }
                                                // ] : []}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Assignment type is required'
                                                    }
                                                ]}
                                            >
                                                <Select options={[
                                                    { label: t("Multiple users, one needed"), value: "ONE" },
                                                    { label: t("Multiple users, all needed"), value: "MULTIPLE" },
                                                    { label: t("Multiple users, one per group"), value: "ONE_PER_GROUP" },
                                                ]
                                                }
                                                />
                                            </Form.Item>
                                            <Form.Item name="approverType" label={formValues?.approvalStep ? t("Approver Selection") : t("Approver Selection")}>
                                                <Radio.Group>
                                                    <Radio value="default">{t("Default")}</Radio>
                                                    <Radio value="conditional">{t("Conditional")}</Radio>
                                                </Radio.Group>
                                            </Form.Item>
                                        {/* </>
                                        : null
                                } */}

                                {
                                    formValues?.approverType === "default" ?
                                        <div >
                                            <div style={{ paddingBottom: "8px" }}>{formValues.approvalStep ? t("Select Approver") : t("Select Assignee")} </div>
                                            <UserRoleDropdown selectedOptions={stepDetails.assignTo ? {
                                                "user": stepDetails?.assignTo?.user || [],
                                                "role": stepDetails?.assignTo?.role || [],
                                                "group": stepDetails?.assignTo?.group || [],
                                                "userPicker": stepDetails?.assignTo?.userPicker || [],

                                            } : { user: [], role: [], group: [], userPicker: [] }}
                                                onSelect={onSelect}
                                                tabsRequired={["user", "group", "role", "userPicker"]}
                                                getSpaceUsers={true} />
                                        </div> : null
                                }

                                <div >
                                    <div style={{ paddingBottom: "8px", marginTop: "16px" }}>{t("Notify To")}</div>
                                    <UserRoleDropdown selectedOptions={stepDetails.notifyTo ? {
                                        "user": stepDetails?.notifyTo?.user || [],
                                        "role": stepDetails?.notifyTo?.role || [],
                                        "group": stepDetails?.notifyTo?.group || [],
                                        "userPicker": stepDetails?.notifyTo?.userPicker || []
                                    } : { user: [], role: [], group: [] }}
                                        onSelect={onNotifySelect}
                                        tabsRequired={["user", "group", "role", "userPicker"]}
                                        getSpaceUsers={true} />
                                </div>

                                {
                                    stepForm ?
                                        <>
                                            <Form.Item
                                                label={t("Share To")}
                                                name="shareTo"
                                                style={{ marginTop: "16px" }}
                                            >
                                                <Select
                                                    allowClear
                                                    mode="multiple"
                                                    style={{ width: "100%" }}
                                                    placeholder={t("Select form fields")}
                                                    options={shareToOptions}
                                                    optionFilterProp="label"
                                                />
                                            </Form.Item>
                                            {
                                                formValues?.shareTo?.length ?
                                                    <Form.Item
                                                        label={t("Share To Language")}
                                                        name="shareLanguage"
                                                        extra={t("Shared users are emailed in the language answered in this field.")}
                                                    >
                                                        <Select
                                                            allowClear
                                                            style={{ width: "100%" }}
                                                            placeholder={t("Select a dropdown field")}
                                                            options={shareLanguageOptions}
                                                            optionFilterProp="label"
                                                        />
                                                    </Form.Item>
                                                    : null
                                            }
                                        </>
                                        : null
                                }
                                </>
                                }
                            </div>

                            <Form.Item className='step-details-action'>
                                <Flex>
                                    <Button htmlType="submit" style={{ width: "9.75rem", marginRight: "16px" }} type="primary" className="submit-button">
                                        {t("Apply")}
                                    </Button>
                                    <Button style={{ width: "9.75rem" }} onClick={closeStepDetails}>{t("Cancel")}</Button>
                                </Flex>
                            </Form.Item>
                        </Flex>
                    </Form>
                </div>
            </Flex>
        </div>
    )
}

export default StepDetailsForm
